import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Contact, Search, ChevronDown, ChevronRight, ArrowRight, ExternalLink } from 'lucide-react';
import { useTenant } from '../components/TenantContext.jsx';
import { customersApi } from '../api/calls.js';
import { PageHeader } from '../components/ui/PageHeader.jsx';
import { Card, Empty, DirectionChip } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';

export default function CustomersPage() {
    const { t } = useTranslation('members');
    const { hostId } = useTenant();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(null); // phone
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!hostId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const r = await customersApi.list(hostId, { limit: 800 });
                if (cancelled) return;
                setItems(r.items || []);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [hostId, reloadKey]);

    const filtered = useMemo(() => {
        if (!search.trim()) return items;
        const q = search.trim().toLowerCase();
        return items.filter((c) =>
            String(c.phone || '').toLowerCase().includes(q) ||
            String(c.lastAvatarName || '').toLowerCase().includes(q) ||
            String(c.lastTransferToName || '').toLowerCase().includes(q)
        );
    }, [items, search]);

    return (
        <div>
            <PageHeader
                eyebrow={hostId ? `tenant · ${hostId}` : 'tenant'}
                title={t('customers.title')}
                description={t('customers.description')}
            />

            <div className="flex items-center gap-3 mb-6">
                <div className="relative flex-1 max-w-md">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                    <input
                        type="text"
                        placeholder={t('customers.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
                    />
                </div>
                <div className="text-xs text-ink-400 mono-caption">
                    {t('customers.countRatio', { shown: filtered.length, total: items.length })}
                </div>
            </div>

            {error && (
                <Card className="bg-danger-100/40 border-danger-500/30 mb-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="text-sm text-danger-500">{error.message}</div>
                        <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>
                            {t('common:actions.retry')}
                        </Button>
                    </div>
                </Card>
            )}

            {loading && items.length === 0 ? (
                <Card>
                    <div className="text-sm text-ink-400 italic text-center py-6">{t('common:state.loading')}</div>
                </Card>
            ) : error && items.length === 0 ? null : filtered.length === 0 ? (
                <Card>
                    <Empty
                        icon={Contact}
                        title={search ? t('customers.emptySearchTitle') : t('customers.emptyTitle')}
                        description={search ? t('customers.emptySearchDesc') : t('customers.emptyDesc')}
                    />
                </Card>
            ) : (
                <Card padded={false}>
                    <table className="w-full">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-ink-400 font-mono">
                                <th className="text-left px-4 py-2.5 font-medium">{t('customers.colPhone')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('customers.colType')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('customers.colAvatar')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('customers.colTags')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('customers.colTransfer')}</th>
                                <th className="text-right px-4 py-2.5 font-medium">{t('customers.colLastCall')}</th>
                                <th className="w-8"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => (
                                <CustomerRow
                                    key={c.phone}
                                    customer={c}
                                    expanded={expanded === c.phone}
                                    onToggle={() =>
                                        setExpanded((cur) => (cur === c.phone ? null : c.phone))
                                    }
                                    hostId={hostId}
                                />
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}
        </div>
    );
}

function CustomerRow({ customer, expanded, onToggle, hostId }) {
    const { t } = useTranslation('members');
    const last = customer.lastCallAt ? new Date(customer.lastCallAt) : null;
    return (
        <>
            <tr
                className={[
                    'border-t border-cream-100 hover:bg-cream-50 cursor-pointer',
                    expanded ? 'bg-cream-50' : '',
                ].join(' ')}
                onClick={onToggle}
            >
                <td className="px-4 py-3 align-top tnum">
                    <div className="text-sm font-medium text-ink-900">{customer.phone}</div>
                    <div className="mono-caption">{t('customers.totalCalls', { n: customer.totalCount })}</div>
                </td>
                <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2 text-xs">
                        {customer.inboundCount > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-accent-50 text-accent-700 rounded-full font-medium">
                                {t('customers.inbound')}
                            </span>
                        )}
                        {customer.outboundCount > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-cream-100 text-ink-700 rounded-full font-medium">
                                {t('customers.outbound')}
                            </span>
                        )}
                    </div>
                </td>
                <td className="px-4 py-3 align-top">
                    {customer.lastAvatarName ? (
                        <>
                            <div className="text-sm text-ink-700 truncate max-w-[140px]">
                                {customer.lastAvatarName}
                            </div>
                            {customer.avatars.length > 1 && (
                                <div className="mono-caption">{t('customers.moreAvatars', { n: customer.avatars.length - 1 })}</div>
                            )}
                        </>
                    ) : null}
                </td>
                <td className="px-4 py-3 align-top">
                    <CustomerTags customer={customer} />
                </td>
                <td className="px-4 py-3 align-top">
                    <Transferees customer={customer} />
                </td>
                <td className="px-4 py-3 align-top text-right">
                    <div className="text-sm text-ink-900 tnum">
                        {last ? last.toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' }) : '—'}
                    </div>
                    <div className="mono-caption">
                        {last ? last.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                </td>
                <td className="px-4 py-3 align-top">
                    <button
                        onClick={onToggle}
                        className="inline-flex p-1.5 rounded hover:bg-cream-200 text-ink-400 transition"
                    >
                        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                </td>
            </tr>
            {expanded && (
                <tr className="bg-cream-100/50 border-t border-cream-200">
                    <td colSpan={7} className="px-6 py-5">
                        <CustomerTimeline phone={customer.phone} hostId={hostId} />
                    </td>
                </tr>
            )}
        </>
    );
}

function CustomerTags({ customer }) {
    const { t } = useTranslation('members');
    const tags = customer.tags || [];
    if (tags.length === 0) return null;
    const VISIBLE = 2;
    const visible = tags.slice(0, VISIBLE);
    const overflow = tags.slice(VISIBLE);
    return (
        <div className="flex flex-wrap gap-1.5 max-w-[200px]">
            {visible.map((tag, i) => (
                <span
                    key={(tag.id || tag.name) + '-' + i}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium"
                    style={{
                        background: tag.color ? tag.color + '22' : '#c84a2c22',   // 透明度 13%
                        color: tag.color || '#c84a2c',
                        border: `1px solid ${tag.color || '#c84a2c'}40`,
                    }}
                    title={t('customers.tagOccurrence', { name: tag.name, n: tag.count })}
                >
                    {tag.name}
                    {tag.count > 1 && <span className="mono-caption" style={{ color: 'inherit', opacity: 0.6 }}>×{tag.count}</span>}
                </span>
            ))}
            {overflow.length > 0 && (
                <span
                    className="inline-flex items-center px-2 py-0.5 bg-cream-200 text-ink-500 text-xs rounded-full font-medium cursor-help"
                    title={overflow.map((tag) => `${tag.name}${tag.count > 1 ? ` ×${tag.count}` : ''}`).join('、')}
                >
                    +{overflow.length}
                </span>
            )}
        </div>
    );
}

function Transferees({ customer }) {
    const { t } = useTranslation('members');
    if (!customer.transferCount) return null;
    const list = customer.transferees || [];
    // 沒 transferees 但有 transferCount（舊資料）→ 用 lastTransferTo 補
    const items = list.length
        ? list
        : customer.lastTransferToExt || customer.lastTransferToName
        ? [{
            ext: customer.lastTransferToExt,
            name: customer.lastTransferToName,
            department: customer.lastTransferToDepartment,
            count: customer.transferCount,
        }]
        : [];
    if (!items.length) {
        return <span className="text-xs text-ink-700">{t('customers.transferredTimes', { n: customer.transferCount })}</span>;
    }
    const VISIBLE = 2;
    const visible = items.slice(0, VISIBLE);
    const overflow = items.slice(VISIBLE);
    return (
        <div className="flex flex-wrap gap-1.5 max-w-[200px]">
            {visible.map((tr, i) => (
                <span
                    key={(tr.ext || tr.name || i) + '-' + i}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-cream-100 text-ink-700 text-xs rounded-full"
                    title={[tr.name, tr.ext && t('customers.extension', { ext: tr.ext }), tr.department, t('customers.callsCount', { n: tr.count })].filter(Boolean).join(' · ')}
                >
                    <span className="font-medium">{tr.name || t('customers.extension', { ext: tr.ext })}</span>
                    {tr.count > 1 && <span className="mono-caption">×{tr.count}</span>}
                </span>
            ))}
            {overflow.length > 0 && (
                <span
                    className="inline-flex items-center px-2 py-0.5 bg-cream-200 text-ink-500 text-xs rounded-full font-medium cursor-help"
                    title={overflow.map((tr) =>
                        [tr.name || t('customers.extension', { ext: tr.ext }), tr.count > 1 ? `×${tr.count}` : ''].filter(Boolean).join(' ')
                    ).join('、')}
                >
                    +{overflow.length}
                </span>
            )}
        </div>
    );
}

function CustomerTimeline({ phone, hostId }) {
    const { t } = useTranslation('members');
    const navigate = useNavigate();
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await customersApi.timeline(hostId, phone, { limit: 100 });
                if (!cancelled) setCalls(r.items || []);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [hostId, phone]);

    if (loading) return <div className="text-xs text-ink-400 italic">{t('customers.loadingTimeline')}</div>;
    if (error) return <div className="text-xs text-danger-500">{error.message}</div>;
    if (!calls.length) return <div className="text-xs text-ink-400 italic">{t('customers.noCallLogs')}</div>;

    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-3">
                {t('customers.timelineHeader', { n: calls.length })}
            </div>
            <div className="space-y-2">
                {calls.map((c) => {
                    const ts = c.startedAt?._seconds ? new Date(c.startedAt._seconds * 1000) : null;
                    return (
                        <div
                            key={c.id}
                            className="flex items-center gap-4 bg-white border border-cream-200 rounded-md px-4 py-3 hover:border-accent-300 hover:bg-cream-50 transition cursor-pointer"
                            onClick={() => navigate(`/calls/${c.id}`)}
                        >
                            <div className="shrink-0 w-28 tnum">
                                <div className="text-sm text-ink-900">
                                    {ts ? ts.toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' }) : '—'}
                                </div>
                                <div className="mono-caption">
                                    {ts ? ts.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }) : ''}
                                </div>
                            </div>
                            <div className="shrink-0">
                                <DirectionChip direction={c.direction} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-ink-700 truncate">
                                    {c.summary || (
                                        <span className="italic text-ink-300">{t('customers.noSummary')}</span>
                                    )}
                                </div>
                                <div className="mono-caption mt-0.5">
                                    {c.avatarName || c.avatarId?.slice(0, 8) || '—'} · {formatDuration(c.durationSec, t)}
                                    {c.audioUrl && ` · ${t('customers.hasRecording')}`}
                                    {c.terminationType === 'transfer' && (
                                        <span className="ml-1 text-accent-700">
                                            · {t('customers.transferredToHuman')}{c.transferToName ? ` ${c.transferToName}` : ''}{c.transferToExt ? t('customers.extensionParen', { ext: c.transferToExt }) : ''}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <ExternalLink size={14} className="text-ink-400 shrink-0" />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function formatDuration(sec, t) {
    if (!sec || sec < 0) return '—';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return t('customers.duration', { m, s: String(s).padStart(2, '0') });
}
